import { Component, OnInit, Input, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FieldControlService, IField, PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import moment from 'moment';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subscription, distinctUntilChanged, filter, debounceTime } from 'rxjs';
import { PhoneService } from '../../services/phone.service';
import { EmailService } from '../../services/email.service';
import { VerifyContactService } from '../../services/verify-contact.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DATE_FORMATS, DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';

const DD_MM_YYYY_FORMAT = {
    parse: { dateInput: 'MM/DD/YYYY' },
    display: {
        dateInput: 'MM/DD/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'MM/DD/YYYY',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'app-popup-message',
    templateUrl: './popup-message.component.html',
    imports: [CommonModule, ...PSIC_COMPONENTS, ReactiveFormsModule, MatDatepickerModule, MatFormFieldModule, MatInputModule, MatIconModule],
    providers: [
        FieldControlService,
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_FORMAT },
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PopupMessageComponent implements OnInit, OnDestroy {
    @Input() popupDispalyObj: any;
    @Input() mobileForm!: UntypedFormGroup;
    @Input() desktopForm!: UntypedFormGroup;
    @Input() insuredInformation: any;
    isMobile: boolean;
    isEmailVerified: boolean = false;
    phoneVerified: boolean = false;
    emailVerified: boolean = false;
    verifyingPhone: boolean = false;
    verifyingEmail: boolean = false;
    popupCallbackDetail: string[] = [];

    get isSaveEnabled(): boolean {
        if (this.popupDispalyObj?.type !== 'actionDialog' || this.popupDispalyObj?.emailVerified !== false) return true;
        const form = this.isMobile ? this.mobileForm : this.desktopForm;
        const sendVia = form?.controls['sendVia']?.value;
        const hasPhone = !!form?.controls['mobileNumber']?.value;
        const hasEmail = !!form?.controls['email']?.value;
        if (this.popupDispalyObj?.useContactVerify) {
            const hasDate = !!form?.controls['effectiveDate']?.value;
            if (!hasDate || (!hasPhone && !hasEmail)) return false;
            if (hasPhone && hasEmail) return this.phoneVerified && this.emailVerified;
            if (hasPhone) return this.phoneVerified;
            return this.emailVerified;
        }
        if (sendVia === 'email') return this.emailVerified;
        if (sendVia === 'sms') return this.phoneVerified;
        if (hasPhone) return this.phoneVerified;
        if (hasEmail) return this.emailVerified;
        return false;
    }

    toDate(value: string): Date {
        return new Date(value.replace(/-/g, '/'));
    }

    private sink = new Subscription();
    private phoneService = inject(PhoneService);
    private emailService = inject(EmailService);
    private verifyContactService = inject(VerifyContactService);

    constructor(
        public bsModalRef: BsModalRef,
        private breakpointObserver: BreakpointObserver,
        private fieldService: FieldControlService,
        private cdr: ChangeDetectorRef
    ) {
        this.isMobile = this.breakpointObserver.isMatched('(max-width: 768px)');
        this.sink.add(
            this.breakpointObserver.observe(['(max-width: 768px)']).subscribe((result) => {
                this.isMobile = result.matches;
            })
        );
    }

    ngOnInit(): void {
        this.mobileForm = this.fieldService.toFormGroup(this.popupDispalyObj.fields as IField[]);
        this.desktopForm = this.fieldService.toFormGroup(this.popupDispalyObj.fields as IField[]);
        this.isEmailVerified = this.popupDispalyObj.emailVerified !== false;

        this.prepopulateFields();

        this.setupFormValueChanges(this.desktopForm, false);
        this.setupFormValueChanges(this.mobileForm, true);

        // Track email field changes — reset verification only when value actually changes
        const desktopEmailControl = this.desktopForm.controls['email'];
        const mobileEmailControl = this.mobileForm.controls['email'];
        if (desktopEmailControl) {
            let lastEmailDesktop = desktopEmailControl.value;
            this.sink.add(
                desktopEmailControl.valueChanges.subscribe((val) => {
                    if (!this.isMobile && val !== lastEmailDesktop) { this.isEmailVerified = false; this.emailVerified = false; }
                    lastEmailDesktop = val;
                })
            );
        }
        if (mobileEmailControl) {
            let lastEmailMobile = mobileEmailControl.value;
            this.sink.add(
                mobileEmailControl.valueChanges.subscribe((val) => {
                    if (this.isMobile && val !== lastEmailMobile) { this.isEmailVerified = false; this.emailVerified = false; }
                    lastEmailMobile = val;
                })
            );
        }

        // Track phone field changes — reset verification only when value actually changes
        const desktopPhoneControl = this.desktopForm.controls['mobileNumber'];
        const mobilePhoneControl = this.mobileForm.controls['mobileNumber'];
        if (desktopPhoneControl) {
            let lastPhoneDesktop = desktopPhoneControl.value;
            this.sink.add(
                desktopPhoneControl.valueChanges.subscribe((val) => {
                    if (!this.isMobile && val !== lastPhoneDesktop) { this.isEmailVerified = false; this.phoneVerified = false; }
                    lastPhoneDesktop = val;
                })
            );
        }
        if (mobilePhoneControl) {
            let lastPhoneMobile = mobilePhoneControl.value;
            this.sink.add(
                mobilePhoneControl.valueChanges.subscribe((val) => {
                    if (this.isMobile && val !== lastPhoneMobile) { this.isEmailVerified = false; this.phoneVerified = false; }
                    lastPhoneMobile = val;
                })
            );
        }

        // Reset both verification flags when sendVia channel changes
        const desktopSendViaControl = this.desktopForm.controls['sendVia'];
        const mobileSendViaControl = this.mobileForm.controls['sendVia'];
        if (desktopSendViaControl) {
            this.sink.add(
                desktopSendViaControl.valueChanges.subscribe(() => {
                    if (!this.isMobile) { this.emailVerified = false; this.phoneVerified = false; this.isEmailVerified = false; this.cdr.markForCheck(); }
                })
            );
        }
        if (mobileSendViaControl) {
            this.sink.add(
                mobileSendViaControl.valueChanges.subscribe(() => {
                    if (this.isMobile) { this.emailVerified = false; this.phoneVerified = false; this.isEmailVerified = false; this.cdr.markForCheck(); }
                })
            );
        }
    }

    formatValue(value: string): string {
        return moment(value).format('MM/DD/YYYY');
    }

    footerButtonAction(event: any) {
        const form = this.isMobile ? this.mobileForm : this.desktopForm;
        const sendViaControl = form?.controls['sendVia'];

        this.popupCallbackDetail = [event];

        if (sendViaControl) {
            this.popupCallbackDetail.push(form.value);
        } else if (this.popupDispalyObj.type === 'actionDialog') {
            this.popupCallbackDetail.push(form.value);
        } else if (form?.valid) {
            const emailControl = form?.controls['email'];
            const phoneControl = form?.controls['mobileNumber'];
            if (emailControl) {
                this.popupCallbackDetail.push(emailControl.value);
            } else if (phoneControl) {
                this.popupCallbackDetail.push(phoneControl.value);
            }
        }

        this.bsModalRef.hide();
    }

    verifyEmail() {
        const form = this.isMobile ? this.mobileForm : this.desktopForm;
        const emailControl = form?.controls['email'];
        if (!emailControl?.value) return;
        this.verifyingEmail = true;
        if (this.popupDispalyObj?.useContactVerify) {
            this.verifyContactService.verifySingleContact(emailControl.value, 'Email').subscribe(({ isValid }) => {
                this.verifyingEmail = false;
                this.emailVerified = isValid;
                this.isEmailVerified = isValid;
                emailControl.setErrors(isValid ? null : { emailService: true });
                emailControl.markAsTouched();
                this.cdr.markForCheck();
            });
        } else {
            this.emailService.validateEmail(emailControl.value).subscribe((errors) => {
                this.verifyingEmail = false;
                const isValid = !errors;
                this.emailVerified = isValid;
                this.isEmailVerified = isValid;
                if (errors?.message) {
                    const emailField = this.popupDispalyObj.fields.find((f: any) => f.name === 'email');
                    if (emailField) {
                        emailField.validationMessages = [{ type: 'emailService', message: errors.message }];
                    }
                }
                emailControl.setErrors(errors ?? null);
                emailControl.markAsTouched();
                this.cdr.markForCheck();
            });
        }
    }

    verifyPhone() {
        const form = this.isMobile ? this.mobileForm : this.desktopForm;
        const phoneControl = form?.controls['mobileNumber'];
        if (!phoneControl?.value) return;
        this.verifyingPhone = true;
        if (this.popupDispalyObj?.useContactVerify) {
            this.verifyContactService.verifySingleContact(phoneControl.value, 'Phone').subscribe(({ isValid, isCellular }) => {
                this.verifyingPhone = false;
                this.phoneVerified = isValid;
                this.isEmailVerified = isValid;
                phoneControl.setErrors(isValid ? null : { phoneService: true });
                phoneControl.markAsTouched();
                this.cdr.markForCheck();
            });
        } else {
            this.phoneService.validatePhone(phoneControl.value).subscribe((errors) => {
                this.verifyingPhone = false;
                const isValid = !errors;
                this.phoneVerified = isValid;
                this.isEmailVerified = isValid;
                phoneControl.setErrors(errors ?? null);
                phoneControl.markAsTouched();
                this.cdr.markForCheck();
            });
        }
    }

    ngOnDestroy(): void {
        this.sink.unsubscribe();
    }

    private prepopulateFields(): void {
        if (!this.insuredInformation) return;

        const emailValue = this.insuredInformation.email?.value;
        const email = Array.isArray(emailValue) ? emailValue[0] : emailValue;
        const phoneValue = this.insuredInformation.phone?.value;
        const phone = Array.isArray(phoneValue) ? phoneValue[0] : phoneValue;
        const cleanPhone = phone ? phone.replace(/\D/g, '') : '';

        if (email && this.desktopForm.controls['email']) {
            this.desktopForm.controls['email'].setValue(email);
        }
        if (email && this.mobileForm.controls['email']) {
            this.mobileForm.controls['email'].setValue(email);
        }
        if (cleanPhone && this.desktopForm.controls['mobileNumber']) {
            this.desktopForm.controls['mobileNumber'].setValue(cleanPhone);
        }
        if (cleanPhone && this.mobileForm.controls['mobileNumber']) {
            this.mobileForm.controls['mobileNumber'].setValue(cleanPhone);
        }
    }

    private setupFormValueChanges(form: UntypedFormGroup, isMobile: boolean): void {
        this.sink.add(
            form.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged(),
                    filter(() => form.valid && this.isMobile === isMobile)
                )
                .subscribe(() => {
                    const uploadDocControl = form.controls['uploadDoc'];
                    if (uploadDocControl) {
                        this.isEmailVerified = !!uploadDocControl.value;
                    }
                })
        );
    }
}
